import { UserService } from '@user/user.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription, from, of } from 'rxjs';
import { Router } from '@angular/router';
import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit, ElementRef } from '@angular/core';
import { IWorkbook, IErrorResponse, IUser } from '@interfaces';
import { WorkbookService } from '@base/workbook/workbook.service';
import { ExceptionService } from '@base/exceptions.service';
import { EMPTY, ROLES } from '@base/utils';
import { AppService } from '@base/app.service';
import { TableauService } from '@base/tableau/tableau.service';
import { map, mergeMap, take, concatMap } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { AngularFireStorage } from '@angular/fire/storage';
import { Store } from '@ngrx/store';
import { RootState } from '@base/rootReducers';
import * as workbookActions from '@workbook/store/workbook.actions';
import * as workbookSelectors from '@workbook/store/workbook.selectors';
import { OpenWorkbookAs } from '@utils';

const BUCKET = environment.buckets;

@Component({
  selector: 'app-tableau-workbook',
  templateUrl: './tableau-workbook.component.html',
  styleUrls: ['./tableau-workbook.component.sass'],
})
export class TableauWorkbookComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[];
  targetUrl = '';
  workbooks: IWorkbook[];
  user: IUser;
  gettingWorkbooks: boolean;
  isHomeUrl: boolean;
  workbookImage = '/assets/limonada.png';
  workbookFallbackImage = '/assets/splash-page-dashboard.png';

  @ViewChild('vizData', { read: ElementRef }) vizData: ElementRef;

  constructor(
    private store: Store<RootState>,
    private router: Router,
    private workbookService: WorkbookService,
    private userService: UserService,
    private exceptionService: ExceptionService,
    private mainService: AppService,
    private tableauService: TableauService,
    private fireStorage: AngularFireStorage,
  ) {
    this.gettingWorkbooks = true;
    this.user = EMPTY;
    this.subscriptions = [];
    this.workbooks = [];
    this.tableauService.setNavBackButtonStatus(false);

    const uSubscription = this.mainService.getCurrentUser().subscribe(
      (u: IUser) => {
        if (u.role['code'] === ROLES.CUSTOMER) {
          this.user = u;
        } else {
          this.router.navigate(['/home']);
        }
      },
      (err: HttpErrorResponse) => { const error: IErrorResponse = this.exceptionService.errorHandling(err); }
    );

    const wSubscription = this.userService.observeUserWorkbooks().pipe(
      map((uWbs: []) => {
        uWbs.map((item: object) => {
          let thumbnailName = '';
          if (item['thumbnail'] !== EMPTY) {
            const urlPrefix = String(item['thumbnail']).substr(0, 5);
            thumbnailName = item['thumbnail'];

            if (urlPrefix === 'https') {
              thumbnailName = this.extractThumbName(String(item['thumbnail']));
            }
          }
          item['thumbnail'] = (item['thumbnail'] === EMPTY) ? EMPTY : thumbnailName;
        });
        return uWbs;
      }))
      .subscribe(
        (w: []) => {
          this.getPreviewImages(w);
        },
        (err: HttpErrorResponse) => {
          const error: IErrorResponse = this.exceptionService.errorHandling(err);
          this.gettingWorkbooks = false;
        }
      );

    this.subscriptions = [...this.subscriptions,
      wSubscription, uSubscription,
    ];
    this.tableauService.setNavBackButtonStatus(false);
    this.tableauService.setDisplayTagsStatus(true);
  }

  ngOnInit() { }

  ngOnDestroy() {
    this.subscriptions.map((s: Subscription) => s.unsubscribe());
  }

  private extractThumbName(strUrl: string): string {
    const auxStrUrl = (' ' + strUrl).slice(1);
    const split1 = auxStrUrl.split('?');
    const token = split1[1];
    const split2 = split1[0].split('%2F');
    const thumbnailName = split2[3];
    return thumbnailName;
  }

  private getPreviewImages(workbookList: []) {
    this.gettingWorkbooks = true;
    const auxWorkbookList = (typeof workbookList !== 'undefined') ? [...workbookList] : [];

    if (workbookList.length) {
      // preparar nombres de las miniaturas...
      let thumbnails = [];
      auxWorkbookList.map(wb => {
        thumbnails = [...thumbnails, wb['thumbnail']];
        wb['show'] = false;
      });
      from(thumbnails)
        .pipe(mergeMap((r) => {
          // obtener la url completa de la imagen desde firebase(Google Storage)
          const uri = `${BUCKET.workbookImages}${r}`;
          return this.fireStorage.ref(uri).getDownloadURL();
        }))
        .subscribe(
          (res: object) => {
            const thumbnailName = this.extractThumbName(String(res));
            const objIndex = auxWorkbookList.findIndex(w => w.thumbnail === thumbnailName);

            if (objIndex >= 0) {
              auxWorkbookList[objIndex].thumbnail = res;
              this.workbooks = auxWorkbookList;
            }

          },
          (err: HttpErrorResponse) => {
            this.gettingWorkbooks = false;
            console.log(err.message);
          },
          () => {
            this.gettingWorkbooks = false;
          }
        );
    } else {
      this.workbooks = auxWorkbookList;
      this.gettingWorkbooks = false;
    }
  }

  openWorkbookViews(w: IWorkbook) {
    if (w.open_as === OpenWorkbookAs.Image) {
      this.workbookService.setWorkbook(w);
      const imageUrl = `/home/panels/tableau/${w.id}/image`;
      this.router.navigate([imageUrl]);
    } else if (w.open_as === OpenWorkbookAs.Report) {
      let params = {isFetching: true, view: null, host: '', ticket: ''};
      this.store.dispatch(workbookActions.getDefaultView({workbookId: w.id}));

      const requests = of(
        this.userService.getTableauParams(this.user),
        this.store.select(workbookSelectors.defaultView).pipe(take(2)),
      );
      const totalRequests = requests.forEach.length;
      let listenerCount = 0;
      const listeners = requests.pipe(concatMap(obs => obs));
      listeners.subscribe(
        (r) => {
          listenerCount++;
          params = Object.assign(params, params, r);

          if (listenerCount === totalRequests) {
            const {isFetching, view, host, ticket} = params;

            // tiene vista inicial fijada
            if (!isFetching && view !== null) {
              this.workbookService.setWorkbook(w);
              const arrayParams = view['content_url'].split('/');
              const viewName = arrayParams.pop();
              const finalUrl = `/home/panels/tableau/${w.id}/views/${viewName}`;
              let targetUrl = `${host}/trusted/${ticket}/views/${w.content_url}/${viewName}`;

              if (w.open_as === OpenWorkbookAs.Report) {
                targetUrl += `?:embed=yes&:toolbar=top&:showShareOptions=false&:scrolling=no`;
                this.workbookService.setFinalUrl(targetUrl);
                this.router.navigate([finalUrl]);
              }
            } else if (!isFetching && view === null) {
              // en caso de no tener vista predeterminada
              this.tableauService.setNavBackButtonStatus(true);
              this.tableauService.setDisplayTagsStatus(false);
              this.workbookService.setWorkbook(w);
              this.router.navigate([`/home/panels/tableau/${w.id}/views`]);
            }
          }
        },
      );    // suscripciones...
    }       // OpenWorkbookAs.Report
  }         // fin del metodo...
}
